/**
 * Nova tarefa (Kanban) — card de natureza 'tarefa', aberto à mão a partir do
 * cardápio. Sem beneficiário obrigatório; sem e-mail/CSAT/NPS (o backend decide
 * pela natureza). O setor é sempre escolhido aqui — nunca inferido.
 */
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { atendimentosAPI, tiposTarefaAPI } from '../services/api';

const PRIORIDADES = [['normal', 'Normal'], ['alta', 'Alta'], ['urgente', 'Urgente']];

export default function NovaTarefaModal({ aberto, onClose, setores, setorInicial, onCriada }) {
  const [tipos, setTipos] = useState([]);
  const [tipoId, setTipoId] = useState('');
  const [setorId, setSetorId] = useState('');
  const [prioridade, setPrioridade] = useState('normal');
  const [motivo, setMotivo] = useState('');
  const [cpf, setCpf] = useState('');
  const [campos, setCampos] = useState({});
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setTipoId(''); setMotivo(''); setCpf(''); setCampos({}); setPrioridade('normal');
    setSetorId(setorInicial ? String(setorInicial) : '');
    tiposTarefaAPI.list({ ativos: true, manual: true })
      .then((r) => setTipos(r.data || []))
      .catch(() => toast.error('Falha ao carregar o cardápio de tarefas'));
  }, [aberto, setorInicial]);

  const tipo = tipos.find((t) => String(t.id) === String(tipoId));
  // Trocar o tipo puxa setor e prioridade padrão do cardápio (o usuário pode mudar)
  const escolherTipo = (id) => {
    setTipoId(id);
    const t = tipos.find((x) => String(x.id) === String(id));
    if (t) {
      if (t.setor_padrao_id && setores.some((s) => String(s.id) === String(t.setor_padrao_id))) setSetorId(String(t.setor_padrao_id));
      setPrioridade(t.prioridade_padrao || 'normal');
      setCampos({});
    }
  };

  const salvar = async () => {
    if (!tipoId) return toast.error('Escolha o tipo da tarefa');
    if (!setorId) return toast.error('Escolha o setor que vai tratar');
    if (!motivo.trim()) return toast.error('Descreva a tarefa');
    for (const c of tipo?.campos || []) {
      if (c.obrigatorio && (campos[c.nome] === undefined || campos[c.nome] === '')) return toast.error(`Preencha "${c.rotulo}"`);
    }
    setSalvando(true);
    try {
      const payload = {
        natureza: 'tarefa', tipo_tarefa_id: Number(tipoId), setor_id: Number(setorId),
        prioridade, motivo: motivo.trim(), canal_entrada: 'sistema', campos_extras: campos, enviar_kanban: true,
      };
      const cpfLimpo = cpf.replace(/\D/g, '');
      if (cpfLimpo) payload.cpf = cpfLimpo;
      const r = await atendimentosAPI.create(payload);
      toast.success(`Tarefa criada! Protocolo: ${r.data.protocolo}`);
      onCriada?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar a tarefa');
    } finally {
      setSalvando(false);
    }
  };

  if (!aberto) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>Nova tarefa</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)', marginTop: 0 }}>
          Tarefa é trabalho interno do setor: não avisa o beneficiário nem dispara pesquisa ao finalizar.
        </p>
        <div className="form-group">
          <label>Tipo (cardápio)</label>
          <select className="form-control" value={tipoId} onChange={(e) => escolherTipo(e.target.value)}>
            <option value="">Selecione…</option>
            {tipos.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
          {tipo?.descricao && <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: 4 }}>{tipo.descricao}</div>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div className="form-group">
            <label>Setor que trata</label>
            <select className="form-control" value={setorId} onChange={(e) => setSetorId(e.target.value)}>
              <option value="">Selecione…</option>
              {setores.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Prioridade</label>
            <select className="form-control" value={prioridade} onChange={(e) => setPrioridade(e.target.value)}>
              {PRIORIDADES.map(([v, r]) => <option key={v} value={v}>{r}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>O que precisa ser feito</label>
          <textarea className="form-control" rows="3" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Descreva a tarefa para quem vai executar…" />
        </div>
        {(tipo?.campos || []).map((c) => (
          <div className="form-group" key={c.nome}>
            <label>{c.rotulo}{c.obrigatorio ? ' *' : ''}</label>
            <input className="form-control" type={c.tipo === 'number' ? 'number' : c.tipo === 'date' ? 'date' : 'text'}
              value={campos[c.nome] ?? ''} onChange={(e) => setCampos({ ...campos, [c.nome]: e.target.value })} />
          </div>
        ))}
        <div className="form-group">
          <label>Beneficiário (opcional): CPF</label>
          <input className="form-control" value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder="Só se a tarefa for sobre uma pessoa" />
        </div>
        {tipo?.checklist?.length > 0 && (
          <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)', marginBottom: '0.75rem' }}>
            <b>Passos deste tipo:</b> {tipo.checklist.join(' · ')}
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={salvando}>Cancelar</button>
          <button className="btn btn-success" onClick={salvar} disabled={salvando}>{salvando ? 'Criando…' : 'Criar tarefa'}</button>
        </div>
      </div>
    </div>
  );
}
